import type { LocationQuery, LocationQueryRaw, RouteLocationRaw } from 'vue-router'
import { useProjectStore } from '@/shared/stores/project'

/** Extracts the projectId from a route query when it is a plain string; arrays and non-strings count as absent. */
export function extractProjectIdFromQuery(query: LocationQuery): string | null {
  return typeof query.projectId === 'string' ? query.projectId : null
}

/**
 * Returns the query with the selected project's id attached, or null when no rewrite is needed
 * (nothing selected, or the URL already carries exactly that id). Pure — the caller decides
 * whether to apply it as a redirect.
 */
export function buildNormalizedProjectQuery(
  query: LocationQuery,
  selectedProjectId: string | null | undefined,
): LocationQueryRaw | null {
  if (!selectedProjectId) {
    return null
  }
  if (typeof query.projectId === 'string' && query.projectId === selectedProjectId) {
    return null
  }
  return { ...query, projectId: selectedProjectId }
}

/**
 * Copies `query` with `projectId` attached for raw-href deep links (router.resolve + window.open).
 * Prefers the explicit id when it is a non-empty string, otherwise falls back to the store's
 * selected project; returns the original query object untouched when neither yields an id.
 */
export function withProjectIdQuery(query: LocationQueryRaw, projectId?: string | null): LocationQueryRaw {
  const resolved =
    projectId && projectId.length > 0 ? projectId : useProjectStore().selectedProjectId
  if (!resolved) {
    return query
  }
  return { ...query, projectId: resolved }
}

export const PROJECT_SELECTION_PATH = '/home/project-selection'

export interface ProjectContextStoreShape {
  selectedProjectId: string | null
  selectProject: (id: string) => Promise<void> | void
  validateSelectedProjectExists: () => Promise<boolean>
  confirmPendingSelection: () => void
}

export interface ResolveProjectContextOptions {
  /** Called when the route must bounce to project selection (the guard clears the navigation intent). */
  onInvalidSelection?: () => void
}

/**
 * Resolves project context for a project-scoped route. Returns `true` to continue, or a redirect
 * target: project selection when no valid project is selected, otherwise the same path with
 * `projectId` normalized. The URL wins over the persisted selection — a differing `projectId`
 * in the query re-selects that project before validation, so the store (and sessionStorage)
 * follows the URL.
 */
export async function resolveProjectContext(
  to: { path: string; query: LocationQuery; hash?: string },
  store: ProjectContextStoreShape,
  options: ResolveProjectContextOptions = {},
): Promise<true | RouteLocationRaw> {
  const projectIdFromQuery = extractProjectIdFromQuery(to.query)
  if (projectIdFromQuery && projectIdFromQuery !== store.selectedProjectId) {
    await store.selectProject(projectIdFromQuery)
  }

  const hasValidSelectedProject = await store.validateSelectedProjectExists()
  if (!hasValidSelectedProject && to.path !== PROJECT_SELECTION_PATH) {
    options.onInvalidSelection?.()
    return { path: PROJECT_SELECTION_PATH }
  }

  store.confirmPendingSelection()

  const normalizedQuery = buildNormalizedProjectQuery(to.query, store.selectedProjectId)
  if (normalizedQuery) {
    return { path: to.path, query: normalizedQuery, hash: to.hash }
  }

  return true
}
